import React from 'react';
import { useQuery } from '@apollo/client';
import { 
  X, 
  FileText, 
  Package,
  User,
  Building,
  Truck,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { SHIPMENT_DETAILS_BY_MANIFEST_QUERY } from '../../services/graphql/queries.js';

const BLsModal = ({ isOpen, onClose, manifest, userRole }) => {
  const { data, loading, error } = useQuery(SHIPMENT_DETAILS_BY_MANIFEST_QUERY, {
    variables: { manifest: manifest || '' },
    skip: !isOpen || !manifest,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network'
  });

  const handleKeyDown = React.useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !manifest) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const shipmentDetails = data?.getShipmentDetailsByManifest || [];
  
  // Additional safety check for data consistency
  const validShipmentDetails = shipmentDetails.filter(detail => 
    detail && detail.bill_of_lading && detail.commercial_invoice
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Conocimientos de Embarque - {manifest}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Detalles de envío asociados al manifiesto
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Cerrar modal"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Cargando detalles del envío...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-500 text-lg mb-2">Error al cargar los detalles</p>
              <p className="text-gray-500 text-sm">{error.message}</p>
            </div>
          ) : validShipmentDetails.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No hay detalles disponibles</p>
              <p className="text-gray-400 text-sm mt-1">
                Este envío no tiene registros de BL asociados
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-900">
                    Resumen del Envío
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 font-medium">Manifiesto:</span>
                    <span className="ml-2 text-blue-900">{manifest}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Referencia:</span>
                    <span className="ml-2 text-blue-900">
                      {validShipmentDetails[0]?.shipment?.reference || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Fecha Creación:</span>
                    <span className="ml-2 text-blue-900">
                      {formatDate(validShipmentDetails[0]?.shipment?.created_at)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Details Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    Detalles del Envío ({validShipmentDetails.length} {validShipmentDetails.length === 1 ? 'registro' : 'registros'})
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Conocimiento de Embarque
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                          Factura Comercial
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Consignatario
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Proveedores
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Freight Forwarder
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vendedor
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          CIF
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {validShipmentDetails.map((detail, index) => (
                        <tr key={detail.bill_of_lading || `detail-${index}`} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-gray-400" />
                              <span className="font-medium text-gray-900">
                                {detail.bill_of_lading}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 w-44 max-w-[176px]">
                            <span className="block truncate" title={detail.commercial_invoice}>
                              {detail.commercial_invoice}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <div>
                                <div className="text-sm font-medium text-gray-900" style={{ whiteSpace: 'pre-line' }}>
                                  {(() => {
                                    const hasConsignee = detail.consignee?.full_name;
                                    const hasAdditional = detail.additional_consignees?.trim();

                                    if (hasConsignee && hasAdditional) {
                                      // Caso: Transportista + consignatarios adicionales
                                      return (
                                        <>
                                          <strong className="text-blue-600">Transportista:</strong> {detail.consignee.full_name} ({detail.consignee.email}){'\n'}
                                          <strong className="text-blue-600">Consignatario:</strong> {detail.additional_consignees}
                                        </>
                                      );
                                    } else if (hasConsignee) {
                                      // Caso: Solo consignee registrado
                                      return detail.consignee.full_name;
                                    } else if (hasAdditional) {
                                      // Caso: Solo consignatarios manuales (modo manual)
                                      return (
                                        <>
                                          <strong className="text-blue-600">Consig. Temporal:</strong> {detail.additional_consignees}
                                        </>
                                      );
                                    } else {
                                      // Caso: Ninguno (no debería ocurrir por validación)
                                      return 'N/A';
                                    }
                                  })()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {(() => {
                                    const hasConsignee = detail.consignee?.full_name;
                                    const hasAdditional = detail.additional_consignees?.trim();

                                    // Si hay Transportista + consignatarios adicionales, no mostrar email (ya está en paréntesis arriba)
                                    if (hasConsignee && hasAdditional) {
                                      return '';
                                    }
                                    // En otros casos, mostrar email si existe
                                    return detail.consignee?.email || '';
                                  })()}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-start space-x-2">
                              <Building className="h-4 w-4 text-gray-400 mt-1" />
                              <div className="flex-1">
                                {detail.suppliers && detail.suppliers.length > 0 ? (
                                  <div className="space-y-1">
                                    {detail.suppliers.map((supplier) => (
                                      <div key={supplier.id} className="text-sm">
                                        <div className="font-medium text-gray-900">{supplier.supplier_name}</div>
                                        <div className="text-xs text-gray-500">
                                          {supplier.email || 'Sin correo asignado'}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-900">N/A</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {detail.ffUser ? (
                              <div className="flex items-center space-x-2">
                                <Truck className="h-4 w-4 text-gray-400" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {detail.ffUser.full_name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {detail.ffUser.email}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500 italic">No aplica</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {detail.sellerUser ? (
                              <div className="flex items-center space-x-2">
                                <User className="h-4 w-4 text-gray-400" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {detail.sellerUser.full_name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {detail.sellerUser.email}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">N/A</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              detail.cif === true
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {detail.cif === true ? 'Sí' : 'No'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default BLsModal;